import deburr from 'lodash.deburr';
import { mapRawRule } from '@pathways/redux/rules/utils';
import { GLOBAL, HOSPITAL } from '@constants';
import {
  IPathway,
  IRawPathway,
  IPathwayIndexEvent,
  IRawPathwayIndexEvent,
  IRawStage,
  IStage,
  IPathwaySnapshot,
  IRawPathwaySnapshot,
  IRawSharedPathwaySnapshot,
  ISharedPathwaySnapshot,
} from './types';

export const generateSlugName = (name: string) =>
  deburr(name.replace(/[^\w-]/g, ''))
    .toLowerCase()
    .replace(/\s/g, '-');

export const mapRawPathway = ({
  index_events: indexEvents,
  is_active: isActive,
  is_deleted: isDeleted,
  num_app_user_pathways: numAppUserPathways,
  metadata,
  stages,
  owner_id: ownerId,
  is_snapshot: isSnapshot,
  is_managed_pathway: isManagedPathway,
  is_readonly: isReadOnly,
  created_from_pathway_id: createdFromPathwayId,
  created_from_pathway_name: createdFromPathwayName,
  created_from_snapshot_number: createdFromSnapshotNumber,
  created_from_snapshot_name: createdFromSnapshotName,
  parent_organisation_slug: parentOrganisationSlug,
  ...pathway
}: IRawPathway): IPathway => ({
  ...pathway,
  indexEvents: indexEvents
    ? indexEvents.map(indexEvent => mapRawPathwayIndexEvent(indexEvent))
    : [],
  isActive,
  isDeleted,
  metadata: metadata || { language: 'en' },
  source: metadata.owner || 'global',
  stages: stages.map(mapRawPathwayStage),
  numAppUserPathways,
  ownerId,
  isSnapshot,
  isManagedPathway,
  isReadOnly,
  createdFromPathwayId,
  createdFromPathwayName,
  createdFromSnapshotNumber,
  createdFromSnapshotName,
  parentOrganisationSlug,
});

export const mapRawPathwayIndexEvent = ({
  event_type_slug: eventTypeSlug,
  rules,
  is_deleted: isDeleted,
  ...indexEvent
}: IRawPathwayIndexEvent): IPathwayIndexEvent => ({
  ...indexEvent,
  eventTypeSlug,
  isDeleted,
  rules: rules.map(rule => mapRawRule(rule)),
});

export const mapRawPathwayStage = ({
  is_adhoc: isAdhoc,
  is_deleted: isDeleted,
  rules,
  ...stage
}: IRawStage): IStage => {
  return {
    ...stage,
    isDeleted,
    isAdhoc,
    rules: rules.map(rule => mapRawRule(rule)),
  };
};

export const updatePathwayMetadata = (metadata: { [key: string]: any }, ownerId: string) => ({
  ...metadata,
  owner: ownerId === GLOBAL ? GLOBAL : HOSPITAL,
  hospitalId: ownerId === GLOBAL ? undefined : ownerId,
});

export const mapRawPathwaySnapshot = (rawSnapshot: IRawPathwaySnapshot): IPathwaySnapshot => {
  const {
    is_snapshot,
    is_shared_snapshot,
    snapshot_number,
    snapshot_name,
    snapshot_description,
    shared_with,
    snapshot_parent,
    ...rawPathway
  } = rawSnapshot;

  return {
    ...mapRawPathway({ ...rawPathway, is_snapshot }),
    isSnapshot: is_snapshot,
    isSharedSnapshot: is_shared_snapshot,
    snapshotParent: snapshot_parent,
    snapshotNumber: snapshot_number,
    snapshotName: snapshot_name,
    snapshotDescription: snapshot_description,
    sharedWith: shared_with,
  };
};

export const mapRawSharedPathwaySnapshot = (
  rawSnapshot: IRawSharedPathwaySnapshot,
): ISharedPathwaySnapshot => {
  const { parent_name, parent_description, parent_index_event_types, ...rest } = rawSnapshot;

  const pathwayMapping = mapRawPathwaySnapshot(rest);

  return {
    ...pathwayMapping,
    parentName: parent_name,
    parentDescription: parent_description,
    parentIndexEventTypes: parent_index_event_types,
    // parentOrganisationSlug is always defined for shared snaphots
    // unlike base pathways where is can be null.
    parentOrganisationSlug: pathwayMapping.parentOrganisationSlug!,
  };
};
